import { Box, Button, Grid, Stack } from '@mui/material';
import { FastField, FieldProps, Form, Formik, useFormikContext } from 'formik';
import { FormattedMessage } from 'react-intl';
import { PaloDetailView, PaloFormSection } from '@synergy/components-palo';
import { PoolDocumentTitle } from '@synergy/components-next';
import { PoolSpinner, PoolTextArea, PoolTypography, ScrollToFieldError } from '@synergy/components';
import { ReactElement, useEffect } from 'react';
import { SaveIcon } from '@synergy/theme-next';
import { tokens } from '@synergy/theme-palo';

import * as FooterSettingsActions from './store/footer-settings-actions';
import { footerLinksSelector, footerSettingsRequestingSelector } from './store/footer-settings-selectors';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

const HELPER_TEXT = {
    serviceLinks: [
        {
            title: {
                de: 'POOLALPIN - Feedback',
            },
            url: 'https://feedback.pool-alpin.work',
            elementId: 'link-feedback',
        },
        {
            title: {
                de: 'starjack - Online-Ticketbuchung',
            },
            url: 'https://starjack.at/',
            elementId: 'link-starjack',
        },
    ],
    newsLinks: [
        {
            title: {
                de: 'Pool-News',
            },
            url: '/news',
            elementId: 'link-news',
        },
    ],
    socialLinks: [
        {
            title: {
                de: 'Instagram',
            },
            url: 'https://www.instagram.com/poolalpin/',
            icon: 'instagram',
            elementId: 'link-instagram',
        },
    ],
};

/** The height of the twenty-row field beside it, less its own caption. */
const TEMPLATE_HEIGHT = 372;

interface FormValues {
    footerLinks: string;
}

export const FooterSettings = (): ReactElement => {
    const footerLinks = useAppSelector(footerLinksSelector);
    const requesting = useAppSelector(footerSettingsRequestingSelector);
    const dispatch = useThunkDispatch();

    useEffect(() => {
        dispatch(FooterSettingsActions.fetchFooterSettings());
    }, [dispatch]);

    const handleSubmit = (values: FormValues) => {
        dispatch(FooterSettingsActions.updateFooterLinks(JSON.parse(values.footerLinks)));
    };

    return (
        <PoolSpinner enabled={requesting} sx={{ width: '100%', height: '100%' }}>
            <Formik initialValues={{ footerLinks }} onSubmit={handleSubmit} enableReinitialize={true}>
                <Form style={{ display: 'contents' }}>
                    <PoolDocumentTitle titleTranslationId={'app.contentManagement.footer.title'} />
                    <ScrollToFieldError />
                    <FooterSettingsView />
                </Form>
            </Formik>
        </PoolSpinner>
    );
};

/** Split out so the header actions can reach the surrounding Formik context. */
const FooterSettingsView = (): ReactElement => {
    const { handleSubmit } = useFormikContext<FormValues>();

    return (
        <PaloDetailView
            title={<FormattedMessage id="app.contentManagement.footer.title" />}
            actions={
                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={() => handleSubmit()}
                >
                    <FormattedMessage id="app.contentManagement.footer.save" />
                </Button>
            }
        >
            <Box m={theme => theme.spacing(2, theme.general.pageContentSpacing)}>
                <PaloFormSection title={<FormattedMessage id="app.contentManagement.footer.subTitle" />}>
                    {/* Both sides hold the same JSON, so they read as one pair:
                        the same box, the same height, the same typeface. The
                        template used to be bare monospace running twice as far
                        down the page as the field it belongs to. */}
                    <Grid container={true} spacing={2}>
                        <Grid item={true} xs={12} md={6}>
                            <FastField name="footerLinks">
                                {({ field }: FieldProps<string>) => (
                                    <PoolTextArea
                                        {...field}
                                        fullWidth={true}
                                        rows={20}
                                        sx={{ '& textarea': { fontFamily: tokens.font.mono, fontSize: '12px' } }}
                                    />
                                )}
                            </FastField>
                        </Grid>
                        <Grid item={true} xs={12} md={6}>
                            <Stack spacing={1}>
                                <PoolTypography id="app.contentManagement.footer.helperText" sx={{ fontWeight: 700 }} />
                                <Box
                                    component="pre"
                                    sx={{
                                        margin: 0,
                                        height: TEMPLATE_HEIGHT,
                                        overflow: 'auto',
                                        padding: '5px 9px',
                                        border: `1px solid ${tokens.color.line}`,
                                        borderRadius: `${tokens.radius.small}px`,
                                        backgroundColor: tokens.color.surfaceAlt,
                                        color: tokens.color.ink2,
                                        fontFamily: tokens.font.mono,
                                        fontSize: '12px',
                                    }}
                                >
                                    {JSON.stringify(HELPER_TEXT, null, 2)}
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>
                </PaloFormSection>
            </Box>
        </PaloDetailView>
    );
};
